import { engaged, gDot, steeringText, type HudReading } from '../core/hud-reading';

const BLANK = '–';

export interface HudDom {
  readonly root: HTMLElement;
  readonly speed: HTMLElement;
  readonly autopilot: HTMLElement;
  readonly brake: HTMLElement;
  readonly pedal: HTMLElement;
  readonly wheel: SVGElement;
  readonly steering: HTMLElement;
  readonly blinkLeft: HTMLElement;
  readonly blinkRight: HTMLElement;
  readonly gDot: SVGCircleElement;
  readonly g: HTMLElement;
}

export class Hud {
  private last: HudReading | undefined;

  constructor(private readonly dom: HudDom) {
    this.clear();
  }

  show(reading: HudReading | undefined): void {
    if (reading === this.last || (reading && this.last && sameReading(reading, this.last))) return;
    this.last = reading;
    if (reading) this.draw(reading);
    else this.clear();
  }

  private clear(): void {
    this.dom.root.dataset['telemetry'] = 'none';
    this.dom.root.querySelectorAll('[data-active]').forEach((indicator) => indicator.removeAttribute('data-active'));
    this.dom.root.querySelectorAll('[data-on]').forEach((indicator) => indicator.removeAttribute('data-on'));
    [this.dom.speed, this.dom.autopilot, this.dom.steering, this.dom.g].forEach((value) => (value.textContent = BLANK));
    this.dom.pedal.style.width = '0%';
    this.dom.wheel.style.transform = '';
    this.dom.gDot.setAttribute('cx', '0');
    this.dom.gDot.setAttribute('cy', '0');
  }

  private draw(reading: HudReading): void {
    this.dom.root.dataset['telemetry'] = 'live';
    this.dom.speed.textContent = reading.speed;
    this.dom.root.querySelectorAll<HTMLElement>('[data-gear]').forEach((gear) => gear.toggleAttribute('data-active', Number(gear.dataset['gear']) === reading.gear));
    this.dom.autopilot.textContent = reading.autopilot;
    this.dom.autopilot.toggleAttribute('data-active', engaged(reading));
    this.dom.brake.toggleAttribute('data-on', reading.brake);
    this.dom.pedal.style.width = `${reading.pedal}%`;
    this.dom.wheel.style.transform = `rotate(${reading.steering}deg)`;
    this.dom.steering.textContent = steeringText(reading);
    this.dom.blinkLeft.toggleAttribute('data-on', reading.blinkLeft);
    this.dom.blinkRight.toggleAttribute('data-on', reading.blinkRight);
    const dot = gDot(reading);
    this.dom.gDot.setAttribute('cx', dot.x.toFixed(2));
    this.dom.gDot.setAttribute('cy', dot.y.toFixed(2));
    this.dom.g.textContent = reading.g;
  }
}

const sameReading = (a: HudReading, b: HudReading): boolean =>
  (Object.keys(a) as (keyof HudReading)[]).every((key) => a[key] === b[key]);
